import { useCallback, useEffect, useMemo, useState } from "react";

import type { DashboardSourceControlRepositoryUpsertRequest } from "../dashboard-client/types.gen";
import type { SourceControlProviderDetail } from "../types";

import {
  deleteSourceControlRepository,
  fetchSourceControlProviderDetail,
  upsertSourceControlRepository,
} from "../api/sourceControl";
import {
  type DashboardSourceControlCredentialCreateRequest,
  type DashboardSourceControlCredentialUpdateRequest,
  createSourceControlCredentialAsUser,
  deleteSourceControlCredentialAsUser,
  updateSourceControlCredentialAsUser,
  verifySourceControlCredentialAsUser,
} from "../dashboard-client";

interface DetailState {
  loading: boolean;
  error: string | null;
  detail: SourceControlProviderDetail | null;
}

const INITIAL_STATE: DetailState = {
  detail: null,
  error: null,
  loading: false,
};

export function useSourceControlProviderDetail(providerId: string | null) {
  const [state, setState] = useState<DetailState>(INITIAL_STATE);

  const loadDetail = useCallback(async () => {
    if (!providerId) {
      setState(INITIAL_STATE);
      return;
    }
    setState((current) => ({ ...current, error: null, loading: true }));
    try {
      const detail = await fetchSourceControlProviderDetail(providerId);
      setState({ detail, error: null, loading: false });
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      setState({ detail: null, error: message, loading: false });
    }
  }, [providerId]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  const createCredential = useCallback(
    async (input: DashboardSourceControlCredentialCreateRequest) => {
      if (!providerId) {
        throw new Error("providerId required");
      }
      const createResult = await createSourceControlCredentialAsUser({
        body: input,
        path: { providerId },
      });
      const { credential } = createResult.data;
      setState((current) =>
        current.detail
          ? {
              ...current,
              detail: {
                ...current.detail,
                credentials: [credential, ...current.detail.credentials],
              },
            }
          : current,
      );
    },
    [providerId],
  );

  const updateCredential = useCallback(
    async (
      credentialId: string,
      input: DashboardSourceControlCredentialUpdateRequest,
    ) => {
      if (!providerId) {
        throw new Error("providerId required");
      }
      const updateResult = await updateSourceControlCredentialAsUser({
        body: input,
        path: { credentialId, providerId },
      });
      const { credential } = updateResult.data;
      setState((current) =>
        current.detail
          ? {
              ...current,
              detail: {
                ...current.detail,
                credentials: current.detail.credentials.map((item) =>
                  item.credential_id === credentialId ? credential : item,
                ),
              },
            }
          : current,
      );
      return credential;
    },
    [providerId],
  );

  const verifyCredential = useCallback(
    async (credentialId: string) => {
      if (!providerId) {
        throw new Error("providerId required");
      }
      const verifyResult = await verifySourceControlCredentialAsUser({
        path: { credentialId, providerId },
      });
      const { credential } = verifyResult.data;
      setState((current) =>
        current.detail
          ? {
              ...current,
              detail: {
                ...current.detail,
                credentials: current.detail.credentials.map((item) =>
                  item.credential_id === credentialId ? credential : item,
                ),
              },
            }
          : current,
      );
      return credential;
    },
    [providerId],
  );

  const deleteCredentialById = useCallback(
    async (credentialId: string) => {
      if (!providerId) {
        throw new Error("providerId required");
      }
      await deleteSourceControlCredentialAsUser({ path: { credentialId } });
      setState((current) =>
        current.detail
          ? {
              ...current,
              detail: {
                ...current.detail,
                credentials: current.detail.credentials.filter(
                  (item) => item.credential_id !== credentialId,
                ),
              },
            }
          : current,
      );
    },
    [providerId],
  );

  const upsertRepositoryEntry = useCallback(
    async (input: DashboardSourceControlRepositoryUpsertRequest) => {
      if (!providerId) {
        throw new Error("providerId required");
      }
      const repository = await upsertSourceControlRepository(providerId, input);
      setState((current) =>
        current.detail
          ? {
              ...current,
              detail: {
                ...current.detail,
                repositories: current.detail.repositories.some(
                  (repo) => repo.repository_id === repository.repository_id,
                )
                  ? current.detail.repositories.map((repo) =>
                      repo.repository_id === repository.repository_id
                        ? repository
                        : repo,
                    )
                  : [repository, ...current.detail.repositories],
              },
            }
          : current,
      );
    },
    [providerId],
  );

  const deleteRepositoryEntry = useCallback(
    async (repositoryId: string) => {
      if (!providerId) {
        throw new Error("providerId required");
      }
      await deleteSourceControlRepository(providerId, repositoryId);
      setState((current) =>
        current.detail
          ? {
              ...current,
              detail: {
                ...current.detail,
                repositories: current.detail.repositories.filter(
                  (repo) => repo.repository_id !== repositoryId,
                ),
              },
            }
          : current,
      );
    },
    [providerId],
  );

  return useMemo(
    () => ({
      ...state,
      createCredential,
      deleteCredential: deleteCredentialById,
      deleteRepository: deleteRepositoryEntry,
      refreshDetail: loadDetail,
      updateCredential,
      upsertRepository: upsertRepositoryEntry,
      verifyCredential,
    }),
    [
      state,
      loadDetail,
      createCredential,
      updateCredential,
      verifyCredential,
      deleteCredentialById,
      upsertRepositoryEntry,
      deleteRepositoryEntry,
    ],
  );
}
